import React from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// MUI
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Icon from '@mui/material/Icon';
import Tooltip from '@mui/material/Tooltip';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material';

type NavDrawerLinkProps = React.PropsWithChildren<{
  open: boolean;
  to: string;
  icon: string;
  ttoken: string;
  // nested?: MenuType[];
  collapse?: () => void;
  collapsed?: boolean;
  level: number;
  name: string;
  bold?: boolean;
  faIconClass?: string;
}>;

const NavDrawerLink = (props: NavDrawerLinkProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { open, to, icon, ttoken, collapse, collapsed, level, name, bold, faIconClass } = props;

  type MyNavLinkProps = Omit<NavLinkProps, 'to'>;

  const MyNavLink = React.useMemo(
    () =>
      // eslint-disable-next-line react/no-unstable-nested-components
      React.forwardRef<HTMLAnchorElement, MyNavLinkProps>((navLinkProps, ref) => {
        const { className: previousClasses, ...rest } = navLinkProps;
        const elementClasses = previousClasses?.toString() ?? '';
        return (
          <NavLink
            
            {...rest}
            key={`nav-link-${name}`}
            ref={ref}
            to={collapse ? '' : to}
            end
            className={({ isActive }) => (isActive && collapse === undefined ? `${elementClasses} Mui-selected` : elementClasses)}
            style={({ isActive }) => ({
              transition: 'box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
              boxShadow: isActive && collapse === undefined ? `inset 3px 0 0 0 ${theme.palette.primary.light}` : undefined,
            })}
          />
        );
      }),
    [name, to]
  );

  const handleClick = () => {
    if (collapse) {
      collapse();
    }
  };

  return (
    <ListItemButton
      sx={{ padding: '8px 8px 8px 12px', gap: '12px' }}
      key={`nav-list-item-${name}`}
      aria-label={`nav-list-item-${name}`}
      component={MyNavLink}
      onClick={handleClick}
    >
      <ListItemIcon
        sx={{
          marginLeft: open ? `${8 * level}px` : '0px',
          transition: 'margin-left 0.3s',
          minWidth: 'unset',
        }}
      >
        {open ? (
          <Icon fontSize="small" color="primary" baseClassName={faIconClass ? 'fas' : undefined} className={faIconClass}>
            {faIconClass ? undefined : icon}
          </Icon>
        ) : (
          <Tooltip title={t(ttoken) as string} placement="top">
            <Icon fontSize="small" color="primary" baseClassName={faIconClass ? 'fas' : undefined} className={faIconClass}>
              {faIconClass ? undefined : icon}
            </Icon>
          </Tooltip>
        )}
      </ListItemIcon>
      <ListItemText slotProps={{ primary: { style: { fontWeight: bold ? 600 : 400 } } }} primary={t(ttoken)} sx={{ m: 0 }} />
      {collapse !== undefined ? (
        collapsed !== undefined && collapsed === true ? (
          <ExpandLess color="disabled" />
        ) : (
          <ExpandMore color="disabled" />
        )
      ) : null}
    </ListItemButton>
  );
};

export default NavDrawerLink;
